import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { compose } from '@cube3/state/src/utils/component-helpers';
import React, { useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { getFormValues, reduxForm } from 'redux-form';
import { useRadioBroadcastMetadataConfig } from '../hooks/useTVBroadcastMetadataConfig';
import { capitalize } from '@material-ui/core';
import { customFieldValidator } from '../utils/broadcastFieldValidators';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import Time from '@cube3/cubicle/src/core/utility/Time';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';

type Meta = {
  advertiser: string;
  first_broadcast_date: string;
  stations: string[];
  title: string;
  commercial_length: string;
  production_company: string;
};

export type RadioExportFormFields = {
  // from api
  title: string;
  client: string;
  product_service: string;
  // hard-coded fields:
  first_broadcast_date: string;
  broadcasters: string[];
  commercial_length: number;
};
interface HOCProps {
  initialValues: RadioExportFormFields;
  loading: boolean;
  previousExport?: {
    stations: string;
    createdAt: string;
  };
}

interface Props extends HOCProps {
  asset: Asset;
}

const normalizeLabel = (field) => {
  if (!field) return '';
  const old = (
    field.system_name ||
    field.display_name ||
    field.label ||
    ''
  ).toLowerCase();
  switch (old) {
    case 'client':
      return 'Advertiser';
    case 'product_service':
      return 'Producer / Audio Studio';
    default:
      return capitalize(old);
  }
};

const mapStateToProps = (state, ownProps: Props) => {
  const { asset, initialValues } = ownProps;

  const form = `radio_export_form__${asset.id}`;

  const values = getFormValues(form)(state);
  return {
    form,
    initialValues: initialValues,
    formValues: values
  };
};

const RadioExportForm = (props: Props) => {
  const { asset, previousExport } = props;

  const length = calcLength(asset);

  const { metadataFields } = useRadioBroadcastMetadataConfig();

  const fields = useMemo(() => {
    const broadcastFields =
      metadataFields?.map((f) => ({
        ...f,
        name: f.system_name,
        label: normalizeLabel(f),
        validate: customFieldValidator
      })) || [];

    return [
      ...broadcastFields,
      {
        name: 'commercial_length',
        label: 'Length',
        type: 'number' as const,
        disabled: length ? true : false,
        required: length ? false : true
      },
      {
        name: 'first_broadcast_date',
        label: 'First broadcast date',
        type: 'date' as const,
        required: true,
        initialFocusedDate: undefined,
        minDate: Date.now(),
        validate: (val) => {
          return !val ? 'Field cannot be empty' : '';
        }
      }
    ] as Field[];
  }, [asset, metadataFields]);

  return (
    <VBox gap={16}>
      {previousExport ? (
        <span>
          <Typography colorVariant='03' variant={SizeVariants.sm}>The asset has been successfully exported to </Typography>
          <Typography weight="label" colorVariant='03' variant={SizeVariants.sm}>{previousExport.stations}</Typography>
          <Typography colorVariant='03' variant={SizeVariants.sm}>{' '}at{' '}</Typography>
          <Time date={previousExport.createdAt} />
        </span>
      ) : null}
      <GenericReduxForm fields={fields} />
    </VBox>
  );
};

const withInitialValues = (Wrapped) => (props) => {
  const { asset } = props;

  const { resources: previousExportBatches, loading: loadingPrevExports } =
    useResourceList__ALPHA({
      resourceId: asset.id,
      resourceType: asset.type,
      edgeType: 'export-batch',
      edgeLabel: 'export-batches',
      params: useRef({
        sort: '-created_at',
        filter: { export_target_type: 'radio' }
      }).current
    });

  const previousExport = useMemo(() => {
    const initial = {
      value: null,
      notice: null
    }
    if (previousExportBatches) {
      // use the latest succeeded export
      const previousExp = previousExportBatches.filter(
        (ex) => ex.attributes.state === 'success'
      )?.[0];
      const meta = previousExp?.attributes.integration_parameters as Meta

      if (!meta) {
        return initial;
      }

      return {
        value: {
          broadcasters: meta.stations || [],
          title: meta.title,
          first_broadcast_date: meta.first_broadcast_date,
          client: meta.advertiser,
          product_service: meta.production_company || ""
        }, notice: {
          stations: (meta.stations ?? []).join(','),
          createdAt: previousExp.attributes.created_at
        }
      };
    }
    return initial;
  }, [previousExportBatches]);

  const {
    broadcastValues: assetBroadcastValues,
    loading: broadcastValuesLoading
  } = useRadioBroadcastMetadataConfig({
    id: asset.id,
    type: asset.type
  });

  const {
    broadcastValues: projectBroadcastValues,
    loading: projectBroadcastValuesLoading
  } = useRadioBroadcastMetadataConfig();

  const loading = broadcastValuesLoading || projectBroadcastValuesLoading;

  const length = calcLength(asset);

  const initialValues = useMemo(() => {
    if (loading || loadingPrevExports) return {};
    // use the project values if no asset values are available
    const values = findValues(assetBroadcastValues, projectBroadcastValues);

    return {
      ...values,
      title: asset.display_name,
      ...(previousExport.value || {}),
      commercial_length: length
    };
  }, [
    asset,
    loading,
    loadingPrevExports,
    previousExport,
    projectBroadcastValues,
    assetBroadcastValues,
    length
  ]);

  if (loading) return null;
  return (
    <Wrapped
      {...props}
      initialValues={initialValues}
      previousExport={previousExport.notice}
    />
  );
};

const calcLength = (asset) =>
  asset.duration ? Math.round(asset.duration / 1000) : 0;

const findValues = (main: {}, fallback: {}) => {
  const allKeys = [...Object.keys(main), ...Object.keys(fallback)];
  return allKeys.reduce((acc, key) => {
    if (main[key] || fallback[key]) {
      acc[key] = main[key] ?? fallback[key];
    }
    return acc;
  }, {});
};

export default compose(RadioExportForm)(
  withInitialValues,
  connect(mapStateToProps),
  reduxForm({
    destroyOnUnmount: true
  })
);
